"use client";

import { X, Mail, Clock, User, AtSign } from "lucide-react";
import React from "react";
import { createPortal } from "react-dom";

export interface EmailData {
  from: string;
  to: string;
  subject: string;
  date: Date | string;
  htmlContent: string;
}

interface EmailPreviewModalProps {
  open: boolean;
  onClose: () => void;
  email: EmailData | null;
}

export const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  open,
  onClose,
  email,
}) => {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Handle Escape key to close modal
  React.useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!mounted || !open || !email) return null;

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(dateObj);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[2147483646] flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* Email client header bar */}
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500">
              <Mail className="h-4 w-4 text-white" />
            </div>
            <span className="font-medium text-gray-700">Gmail</span>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 transition-colors hover:bg-gray-200"
            aria-label="Close email"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Email headers */}
        <div className="border-b border-gray-200 bg-white px-6 py-4">
          <h1 className="mb-4 text-xl font-semibold text-gray-900">
            {email.subject}
          </h1>

          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                <User className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-baseline justify-between">
                  <span className="font-medium text-gray-900">
                    {email.from}
                  </span>
                  <div className="flex items-center gap-1 text-gray-500">
                    <Clock className="h-3 w-3" />
                    <span className="text-xs">{formatDate(email.date)}</span>
                  </div>
                </div>
                <div className="mt-0.5 flex items-center gap-1 text-gray-500">
                  <AtSign className="h-3 w-3" />
                  <span>to {email.to}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Email content - rendered HTML */}
        <div className="flex-1 overflow-auto bg-white">
          <iframe
            srcDoc={email.htmlContent}
            title="Email content"
            className="h-full w-full border-0"
            sandbox="allow-same-origin"
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

// Email preview context for managing modal state
interface EmailPreviewContextType {
  openEmailPreview: (email: EmailData) => void;
  closeEmailPreview: () => void;
}

const EmailPreviewContext = React.createContext<EmailPreviewContextType | null>(
  null
);

export const useEmailPreview = () => {
  const context = React.useContext(EmailPreviewContext);
  if (!context) {
    throw new Error("useEmailPreview must be used within EmailPreviewProvider");
  }
  return context;
};

export const EmailPreviewProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [email, setEmail] = React.useState<EmailData | null>(null);
  const [open, setOpen] = React.useState(false);

  const openEmailPreview = React.useCallback((emailData: EmailData) => {
    setEmail(emailData);
    setOpen(true);
  }, []);

  const closeEmailPreview = React.useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <EmailPreviewContext.Provider
      value={{ openEmailPreview, closeEmailPreview }}
    >
      {children}
      <EmailPreviewModal
        email={email}
        open={open}
        onClose={closeEmailPreview}
      />
    </EmailPreviewContext.Provider>
  );
};
